// Навигация по продуктам (двери рельса, адресация URL) + меню разделов активной
// двери — контракт .claude/specs/2026-08-09-v6-menu-contract.md.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as platformMenu from "../api/platformMenu";
import { toRailMenu } from "../rail/railMenu";
import { normalizeScreenSections, sectionsEqual } from "./normalize";
import { createOsnovaContext, sharedEventBus } from "../osnovaContext";
import { t } from "../i18n/i18n";
import { productTextApi, resolveMenu } from "../i18n/productDict";
import { useProductDictRevision } from "../rail/useProducts";
import { productSlugsKey } from "./productSlugs";
import { parseEntryFromPath, resolveDoorEntry } from "./deeplink";
import { createAccessGate } from "./accessGate";
// «Магазин продуктов» — строка панели (правило 4 контракта), не продукт: свой слаг.
export const STORE_SLUG = "store";
// Синтетический пункт «Мой профиль» в собственном меню продукта-владельца (role:"profile" в
// products, скрыт с рельса) — раскрывающийся, с подпунктами-вкладками ящика профиля; клик по
// нему/детям не меняет раздел продукта, а открывает ящик (см. pickItem, useProfileDrawer).
export const PROFILE_MENU_ITEM_ID = "profile";
export const PROFILE_TAB_PREFIX = "profile:";
const PROFILE_TAB_IDS = ["general", "absence", "settings"];
// Литеральные ключи словаря вместо шаблона — R5: сторож i18nlint ищет ключи статическим анализом.
const PROFILE_TAB_LABEL_KEYS = {
    general: "shell.profile.tab_general",
    absence: "shell.profile.tab_absence",
    settings: "shell.profile.tab_settings",
};
// Дефолтный набор вкладок профиля Оболочки — когда паспорт продукта-владельца не объявил
// profile_screen.tabs (T-245 Ф6б-Оболочка); заголовки — ключи словаря Оболочки, не паспорта.
const DEFAULT_PROFILE_TABS = PROFILE_TAB_IDS.map((id) => ({
    id,
    label: PROFILE_TAB_LABEL_KEYS[id],
}));
// «/account» — легаси-алиас снятого продукта settings.account (products_removed_2026_09_01,
// см. CLAUDE.md п.1): в каталоге такой двери больше нет, голый URL вёл на «Доступ запрещён»
// (openProduct("account") → 403). Правильный адрес того же смысла — ящик профиля Оболочки,
// не отдельная дверь (B47) — снимаем мусорный путь из адресной строки и открываем профиль
// событием той же шины, что аватар шапки (useProfileDrawer.ts); эмит — следующим тиком, чтобы
// слушатель успел смонтироваться (событие эмитится синхронно из лениво инициализируемого стейта,
// раньше первого рендера MainShell/useProfileDrawer).
function parsePathname() {
    const pathname = window.location.pathname;
    if (pathname === "/account" || pathname.startsWith("/account/")) {
        window.history.replaceState(null, "", "/");
        window.setTimeout(() => sharedEventBus.emit("platform:open-profile"), 0);
        return null;
    }
    return parseEntryFromPath(pathname);
}
// Инвариант «разделы живут вместе со своей дверью» вынесен в doorSections.ts (тикет 64) —
// в ES-модуле место объявления импорта на порядок загрузки не влияет.
import { screenSectionsForDoor, acceptsDoorSections } from "./doorSections";
export function useProductMenu({ products, productsReady, user, theme, locale, numFmt, openModal }) {
    const [entry, setEntry] = useState(parsePathname);
    // Меню из паспорта хранится с ключами; текст подставляется на чтении (productMenu ниже).
    const [rawProductMenu, setProductMenu] = useState(null);
    const dictRevision = useProductDictRevision();
    const [menuLoading, setMenuLoading] = useState(false);
    // Правило 1 контракта: как только смонтированный экран объявил разделы через
    // ctx.onSections, панель берёт их вместо getProductMenu (полная замена, не мерж) —
    // экран шлёт их через sharedEventBus ("platform:sections"), Shell слушает и подменяет.
    const [screenSectionsHeld, setScreenSectionsHeld] = useState(null);
    const [activeItemId, setActiveItemId] = useState(null);
    // Раздел, доставляемый новому продукту как стартовый view сразу при монтировании (см.
    // activeModuleOwnerRef ниже) — деплинк из URL (resolveDoorEntry) или явный клик по пункту меню.
    const pendingSectionRef = useRef(null);
    // Меню продуктов, уже полученные getProductMenu в этой сессии, по id — повторный
    // заход на тот же продукт (прямой ссылкой, после reload SPA-роутинга или снова
    // через рельс) берёт готовое, не запрашивая сеть заново.
    const menuCacheRef = useRef(new Map());
    // Последние живые разделы, объявленные экраном, ПО КАЖДОЙ двери (T-SHELL-DOOR-SECTIONS) —
    // при возврате на дверь показываются сразу (см. блок смены владельца меню ниже), паспортный
    // список — только пока живых для этой двери ещё не было.
    const sectionsCacheRef = useRef(new Map());
    // Последний выбранный раздел ПО КАЖДОЙ двери (T-SHELL-DOOR-SWITCH-CONTENT) — клик по двери в
    // рельсе, на которой уже были в этой сессии, возвращает на раздел, где остановились, а не
    // сбрасывает на паспортный дефолт (см. блоки activeModuleOwnerRef/seededDoorRef/lastEntrySeenRef).
    const lastItemForDoorRef = useRef(new Map());
    const [denied, setDenied] = useState(false);
    // Шлюз openProduct (accessGate.ts) — переживает смену двери, один экземпляр на вкладку.
    const accessGateRef = useRef(createAccessGate());
    const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
    const [, setBreadcrumbs] = useState([]);
    useEffect(() => {
        const onPop = () => setEntry(parsePathname());
        window.addEventListener("popstate", onPop);
        return () => window.removeEventListener("popstate", onPop);
    }, []);
    // Мобильный ящик меню — закрытие по Esc (канон preview-platform.jsx).
    useEffect(() => {
        if (!mobileSidebarOpen)
            return;
        const onKey = (e) => { if (e.key === "Escape")
            setMobileSidebarOpen(false); };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [mobileSidebarOpen]);
    // target.module — составной адрес рельса (entryId продукта, "<slug>[/<item>]"); target.path
    // (NavApi.open, osnovaContext.ts) — хвост адреса ПОСЛЕ двери, resolveDoorEntry/deeplink.ts
    // отдаёт его продукту как section целиком (та же логика, что и деплинк из URL) — без него
    // адресат кросс-продуктового перехода (напр. «написать сотруднику» → Чат) терялся.
    // CSx10: каждый сегмент (module и path) кодируется отдельно encodeURIComponent, не путь
    // целиком — иначе "/" внутри самого сегмента (или произвольный символ адреса, напр. person_ref
    // с "/") ломал бы разбор deeplink.ts (parseEntryFromPath decodeURIComponent тоже посегментно).
    const navigate = useCallback((target) => {
        const segments = target.path ? `${target.module}/${target.path}` : target.module;
        const url = "/" + segments.split("/").map(encodeURIComponent).join("/");
        window.history.pushState(null, "", url);
        setEntry(parsePathname());
        setMobileSidebarOpen(false);
    }, []);
    // Слаги установленных продуктов (СВОД п.32, osnovaContext.ts:installedProducts) — по полю
    // product, не по id записи рельса: один продукт может дать несколько дверей (personnel/personnel-hr),
    // а «установлен» проверяется по продукту, не по конкретной двери. Мемоизация — по СОДЕРЖИМОМУ
    // (installedKey, productSlugsKey выше), не по ссылке products: живые опросы каталога меняют
    // ссылку на каждый тик без смены состава продуктов (T-XPRODUCT-DISABLED, ModuleHost.tsx:
    // ctx?.installedProducts в deps эффекта updateCtx).
    const installedKey = productSlugsKey(products);
    const installedProducts = useMemo(() => (installedKey ? installedKey.split(",") : []), [installedKey]);
    const resolvedEntry = entry ? resolveDoorEntry(entry, products) : null;
    const activeEntryId = resolvedEntry?.entryId ?? null;
    const activeProduct = products.find((p) => p.id === activeEntryId) ?? null;
    // Первый сегмент URL по построению равен слагу продукта (Design Decision 1) — используется,
    // пока каталог ещё не загружен и activeProduct.product недоступен, дальше они совпадают.
    const activeModule = activeProduct?.product ?? entry?.slug ?? null;
    // Меню с текстом: ключи паспорта → словарь продукта; пункт без текста — «Без названия», раздел — без заголовка.
    const productMenu = useMemo(() => (rawProductMenu && activeProduct ? resolveMenu(rawProductMenu, activeProduct.product) : null), [rawProductMenu, activeProduct, dictRevision]);
    // Отражает productMenu без задержки эффекта — читает обработчик onSections (см. ниже),
    // чтобы сравнивать с самым свежим паспортным меню, а не с тем, что было при подписке.
    const productMenuRef = useRef(null);
    useEffect(() => { productMenuRef.current = productMenu; }, [productMenu]);
    // Текущая дверь, видимая слушателю шины НЕМЕДЛЕННО (см. acceptsDoorSections выше): присваивание
    // в рендере идемпотентно, поэтому перезапуск рендера его не портит.
    const activeDoorRef = useRef(activeEntryId);
    activeDoorRef.current = activeEntryId;
    // Снимок каталога для pickProduct (T-286 §3) — без него useCallback держал бы products в deps,
    // пересобирая pickProduct на каждый живой тик рельса (ссылка products меняется чаще состава,
    // T-XPRODUCT-DISABLED), что дёргало бы RailNode/ProductRail лишним реконсиляцией.
    const productsRef = useRef(products);
    productsRef.current = products;
    // Раздел деплинка (см. resolveDoorEntry) доставляется в pendingSectionRef РОВНО ОДИН РАЗ на
    // конкретный сырой URL (sectionDeeplinkRef хранит entry.entryId "как в адресной строке") —
    // без этой защёлки resolvedEntry пересчитывался бы на каждый рендер, пока URL не сменится, и
    // перетирал бы pendingSectionRef.current уже ПОСЛЕ того, как его забрал и очистил блок ниже
    // (seededDoorRef/activeModuleOwnerRef), портя раздел следующей, уже никак не связанной двери.
    const sectionDeeplinkRef = useRef(null);
    if (resolvedEntry?.section && entry && sectionDeeplinkRef.current !== entry.entryId) {
        sectionDeeplinkRef.current = entry.entryId;
        pendingSectionRef.current = resolvedEntry.section;
    }
    useEffect(() => { setBreadcrumbs([]); }, [activeEntryId]);
    // Сброс activeItemId — СИНХРОННО в рендере (не в эффекте): ModuleHost
    // монтирует новый продукт своим child-эффектом РАНЬШЕ, чем успел бы
    // отработать эффект ниже — без этого новый экран получает activeItemId
    // ПРЕДЫДУЩЕГО продукта в ctx.section при самом первом mount(). Ключ — activeModule
    // (не entryId): ремоунт ModuleHost триггерит смена именно модуля (Design Decision 3).
    // pendingSectionRef доставляет раздел, выбранный явным кликом по пункту меню, — новый продукт
    // монтируется сразу с этим view (см. pickItem); иначе — раздел, на котором дверь оставалась в
    // этой сессии (lastItemForDoorRef, T-SHELL-DOOR-SWITCH-CONTENT), а если двери ещё не было —
    // стартовая секция пункта (p.section).
    const activeModuleOwnerRef = useRef(activeModule);
    if (activeModuleOwnerRef.current !== activeModule) {
        activeModuleOwnerRef.current = activeModule;
        const remembered = activeEntryId ? lastItemForDoorRef.current.get(activeEntryId) : undefined;
        setActiveItemId(pendingSectionRef.current ?? remembered ?? activeProduct?.section ?? null);
        pendingSectionRef.current = null;
    }
    // Досев стартового раздела двери, когда каталог продуктов ещё не был загружен на момент
    // блока выше (F5 на прямом URL двери, напр. /personnel/hr): в первом рендере activeProduct
    // ещё null, activeModule берётся из URL-фолбэка и НЕ меняется, когда каталог подгружается
    // (тот же слаг), поэтому блок выше не срабатывает и activeItemId остаётся null навсегда —
    // экран продукта (entry.tsx) тогда сам подставляет чужой дефолт раздела ("Команда" вместо
    // "Управление персоналом"). СИНХРОННО в рендере (тот же приём, что выше), один раз на дверь
    // (seededDoorRef), и только пока раздел ещё не выбран явно — иначе будущий клик/переход
    // будет затёрт паспортным дефолтом задним числом. pendingSectionRef (T-SHELL-SECTION-DEEPLINK,
    // resolveDoorEntry выше) — раздел прямо из URL (/settings/backups) — приоритетнее паспортного.
    const seededDoorRef = useRef(null);
    if (activeEntryId && activeItemId === null && seededDoorRef.current !== activeEntryId) {
        const remembered = lastItemForDoorRef.current.get(activeEntryId);
        if (pendingSectionRef.current || remembered || activeProduct?.section) {
            seededDoorRef.current = activeEntryId;
            setActiveItemId(pendingSectionRef.current ?? remembered ?? activeProduct?.section ?? null);
            pendingSectionRef.current = null;
        }
    }
    // Переключение пунктов ОДНОГО продукта (entryId меняется, activeModule — нет) не ремонтирует
    // ModuleHost — уже смонтированный экран узнаёт новую стартовую секцию пункта живьём той же
    // шиной, что и клик по разделу панели (pickItem). Два пункта рельса ОДНОГО продукта
    // (menu_items[] с двумя записями в паспорте) дают именно этот случай, а не
    // смену activeModule, — pendingSectionRef (раздел, выбранный явным кликом по пункту меню) обязан
    // быть применён здесь так же, как в синхронном блоке выше, иначе клик по НЕпервому пункту
    // затирается стартовым разделом пункта.
    const lastEntrySeenRef = useRef({ entryId: null, module: null });
    useEffect(() => {
        // До готовности каталога activeEntryId — сырой URL-фолбэк (entry.entryId), который
        // resolveDoorEntry (T-SHELL-SECTION-DEEPLINK) может ПЕРЕСЧИТАТЬ на другое значение, как
        // только каталог подгрузится (/settings/backups: "settings/backups" → "settings"), без
        // реального клика по рельсу. Не запоминать этот сырой снимок как prev — иначе первый же
        // "настоящий" рендер после готовности каталога ловится веткой ниже как переключение пункта
        // ОДНОГО продукта и затирает уже засеянный из URL раздел паспортным дефолтом.
        if (!productsReady)
            return;
        const prev = lastEntrySeenRef.current;
        lastEntrySeenRef.current = { entryId: activeEntryId, module: activeModule };
        if (prev.entryId === activeEntryId)
            return;
        if (!prev.module || prev.module !== activeModule || !activeModule)
            return;
        const remembered = activeEntryId ? lastItemForDoorRef.current.get(activeEntryId) : undefined;
        const section = pendingSectionRef.current ?? remembered ?? activeProduct?.section ?? null;
        pendingSectionRef.current = null;
        setActiveItemId(section);
        sharedEventBus.emit("platform:section", { productId: activeModule, section });
    }, [productsReady, activeEntryId, activeModule, activeProduct]);
    // Голый URL продукта без пункта в списке (нет primary-записи без id) — берём первую по
    // order запись того же продукта и канонизируем адрес (Design Decision 1, §1.6 плана). T-286 §3:
    // в адрес уходит только слаг продукта и стартовый раздел найденной записи — id входа (её
    // составной id, "<slug>/<item>") больше не дописывается, его решает каталог (resolveDoorEntry).
    useEffect(() => {
        if (!productsReady || !entry || entry.hasItem)
            return;
        if (products.some((p) => p.id === entry.entryId))
            return;
        const candidates = products.filter((p) => p.product === entry.slug && !p.role);
        if (candidates.length === 0)
            return;
        const primary = candidates.reduce((a, b) => ((b.order ?? 0) < (a.order ?? 0) ? b : a));
        const segments = primary.section ? [entry.slug, primary.section] : [entry.slug];
        window.history.replaceState(null, "", "/" + segments.map(encodeURIComponent).join("/"));
        setEntry(parsePathname());
    }, [productsReady, products, entry]);
    // Меню продукта + шов onSections: сброс override при смене продукта,
    // подписка на события смонтированного экрана, фолбэк на getProductMenu.
    // Сброс/подсев меню продукта — СИНХРОННО в рендере (тот же приём, что и activeModuleOwnerRef
    // выше): эффект ниже коммитится ПОСЛЕ отрисовки, поэтому сброс внутри него даёт один лишний
    // кадр со старым меню ПРЕЖНЕГО продукта при переключении двери.
    const menuOwnerRef = useRef(activeEntryId);
    if (menuOwnerRef.current !== activeEntryId) {
        menuOwnerRef.current = activeEntryId;
        const cached = activeEntryId ? menuCacheRef.current.get(activeEntryId) : undefined;
        if (cached) {
            setProductMenu(cached);
            setMenuLoading(false);
        }
        else {
            setProductMenu(null);
        }
        // Живые разделы этой двери из прошлого визита (если экран уже успел их объявить) —
        // показываем сразу, паспортный список подставится сам (menuForRail), если кеша нет.
        const cachedSections = activeEntryId ? sectionsCacheRef.current.get(activeEntryId) : undefined;
        setScreenSectionsHeld(activeEntryId && cachedSections ? { doorId: activeEntryId, sections: cachedSections } : null);
        setDenied(false);
    }
    useEffect(() => {
        if (!activeEntryId || activeEntryId === STORE_SLUG || !activeProduct) {
            setMenuLoading(false);
            return;
        }
        if (activeProduct.status !== "ok" || activeProduct.hasMenu === false) {
            setMenuLoading(false);
            return;
        }
        let ignore = false;
        // Меню уже подсеяно из сессионного кеша (тот же id, см. блок выше) — повторный запрос не нужен.
        if (productMenuRef.current == null) {
            setMenuLoading(true);
            platformMenu.getProductMenu(activeProduct.id).then((m) => {
                if (ignore)
                    return;
                menuCacheRef.current.set(activeProduct.id, m);
                setProductMenu(m);
                setMenuLoading(false);
            }).catch(() => { if (!ignore)
                setMenuLoading(false); });
        }
        const off = sharedEventBus.on("platform:sections", (data) => {
            const payload = data;
            if (!payload || payload.productId !== activeProduct.id)
                return;
            // Дверь сверяется ещё и с текущей на момент события: этот слушатель живёт до пассивной
            // фазы уже после смены двери, а уходящий экран в это время ещё жив и может эмитить.
            if (!acceptsDoorSections(activeDoorRef.current, payload.productId))
                return;
            const next = normalizeScreenSections(payload.sections);
            if (next)
                sectionsCacheRef.current.set(activeProduct.id, next);
            // Экран (обычно) объявляет то же содержимое, что уже показано из паспорта, — не подменяем
            // ссылку и не дёргаем панель видимым перестроением, если содержимое не изменилось.
            setScreenSectionsHeld((prev) => {
                const own = prev && prev.doorId === activeProduct.id ? prev : null;
                if (sectionsEqual(own?.sections ?? productMenuRef.current?.sections, next))
                    return own;
                return next ? { doorId: activeProduct.id, sections: next } : null;
            });
        });
        return () => { ignore = true; off(); };
    }, [activeEntryId, activeProduct]);
    // Известен адрес, продукты загружены, но такой записи в списке нет — либо чужой прямой
    // заход (openProduct → denied), либо продукт без экрана (паспорт без front, нет menu_items —
    // в каталог не попадает вовсе, та же ветка), либо доступ закрыт. Голый URL без primary-записи
    // ждёт редиректа эффектом выше — сюда его пускать незачем (иначе denied мигнёт раньше редиректа).
    // accessGate (T-SHELL-OPENPRODUCT-STORM) — POST уходит один раз на activeEntryId за вкладку:
    // без него эффект перезапускается на каждый живой кадр рельса (ссылка products меняется на
    // каждый тик useProducts.ts, даже когда состав каталога не поменялся) и штормит ядро повтором.
    useEffect(() => {
        if (!productsReady || !activeEntryId || activeEntryId === STORE_SLUG)
            return;
        if (products.some((p) => p.id === activeEntryId))
            return;
        if (entry && !entry.hasItem && products.some((p) => p.product === entry.slug && !p.role))
            return;
        if (!accessGateRef.current.shouldCheck(activeEntryId)) {
            // Повторный заход на уже проверенный в этой сессии id — готовый результат, без сети.
            setDenied(accessGateRef.current.cached(activeEntryId) ?? false);
            return;
        }
        let ignore = false;
        platformMenu.openProduct(activeEntryId)
            .then((r) => {
            const isDenied = "denied" in r;
            accessGateRef.current.markResolved(activeEntryId, isDenied);
            if (!ignore)
                setDenied(isDenied);
        })
            .catch(() => {
            accessGateRef.current.markResolved(activeEntryId, true);
            if (!ignore)
                setDenied(true);
        });
        return () => { ignore = true; };
    }, [productsReady, activeEntryId, products, entry]);
    // Правило 1: панель не ждёт getProductMenu, если экран уже объявил разделы сам — но только
    // разделы ЭТОЙ двери (screenSectionsForDoor, тикет 64). productId несётся в панель и дальше:
    // ProductRail (kit: menuReady) сам не рисует меню чужого продукта — сверка на обоих концах.
    const screenSections = screenSectionsForDoor(screenSectionsHeld, activeEntryId);
    const menuForRail = screenSections
        ? { title: productMenu?.title ?? activeProduct?.label, sub: productMenu?.sub, sections: screenSections, productId: activeEntryId ?? undefined }
        : productMenu;
    const shownSlug = activeEntryId;
    const shownMenuRaw = menuForRail;
    // Продукт, чьё меню сейчас показано, — по нему ищем синтезированную запись профиля
    // (role:"profile", тот же product.slug) для пункта «Мой профиль» в СВОЁМ меню продукта
    // (design: OSNOVA - Команда.html — пункт с детьми над разделами, не отдельный рельс).
    const shownProduct = products.find((p) => p.id === shownSlug) ?? null;
    // «Мой профиль» — только у ПЕРВИЧНОЙ двери продукта (id совпадает со слагом product, primary
    // menu_items без "id", entryID() в manifest.go): у продукта с двумя дверьми (personnel —
    // «Команда» + «Управление персоналом») пункт живёт в «Команде» по дизайну, не дублируется во
    // вторую дверь того же продукта (10-design-up-default.png — под УП «Мой профиль» отсутствует).
    const profileSibling = shownProduct && shownProduct.id === shownProduct.product
        ? products.find((p) => p.product === shownProduct.product && p.role === "profile") ?? null
        : null;
    const profileMenuItem = useMemo(() => {
        if (!profileSibling)
            return null;
        // Вкладки берём из паспорта продукта-владельца (profile_screen.tabs, текст — как пришёл,
        // тот же канал, что заголовки menu_items); паспорт их не объявил — дефолт Оболочки из словаря.
        const tabs = profileSibling.tabs && profileSibling.tabs.length > 0
            ? profileSibling.tabs.map((tab) => ({ id: tab.id, label: tab.title }))
            : DEFAULT_PROFILE_TABS.map((tab) => ({ id: tab.id, label: t(tab.label) }));
        return {
            id: PROFILE_MENU_ITEM_ID,
            label: profileSibling.label,
            icon: profileSibling.icon,
            children: tabs.map((tab) => ({ id: `${PROFILE_TAB_PREFIX}${tab.id}`, label: tab.label })),
        };
    }, [profileSibling, locale]);
    const shownMenu = useMemo(() => {
        if (!shownMenuRaw || !profileMenuItem)
            return shownMenuRaw;
        const sections = shownMenuRaw.sections ?? [];
        if (sections.length === 0)
            return { ...shownMenuRaw, sections: [{ items: [profileMenuItem] }] };
        const [first, ...rest] = sections;
        return { ...shownMenuRaw, sections: [{ ...first, items: [profileMenuItem, ...(first.items ?? [])] }, ...rest] };
    }, [shownMenuRaw, profileMenuItem]);
    const shownLoading = menuLoading;
    const shownActiveItemId = activeItemId;
    const railMenu = toRailMenu(shownMenu);
    const railSections = railMenu?.sections ?? [];
    const railHasItems = railSections.some((s) => (s.items ?? []).length > 0);
    // Панель открытого продукта без разделов сворачивается (продукт открывается сразу,
    // второй слой ему не нужен) — тот же расчёт, что в kit/v5/src/preview-platform.jsx.
    const collapsedMenu = !shownLoading && !railHasItems;
    // Правило 2 контракта: клик по пункту панели напрямую выставляет section
    // экрана — общий словарь id, без таблицы соответствий; то же самое поле
    // меняет и сам экран через ctx.onSectionChange (единый сеттер).
    // Клик по рельсу меняет раздел смонтированного экрана ЖИВО, без ремоунта
    // ModuleHost (тот больше не следит за view) — экран получает новый section
    // через ту же шину, на которую подписан изнутри (entry.tsx каждого продукта).
    const pickItem = useCallback((item) => {
        // «Мой профиль» и его вкладки — синтетический пункт (см. profileMenuItem выше), не раздел
        // продукта: открывает ящик профиля (useProfileDrawer, platform:open-profile), section не трогает.
        if (item.id === PROFILE_MENU_ITEM_ID || item.id.startsWith(PROFILE_TAB_PREFIX)) {
            const tab = item.id === PROFILE_MENU_ITEM_ID ? PROFILE_TAB_IDS[0] : item.id.slice(PROFILE_TAB_PREFIX.length);
            sharedEventBus.emit("platform:open-profile", { tab });
            setMobileSidebarOpen(false);
            return;
        }
        setActiveItemId(item.id);
        if (activeModule)
            sharedEventBus.emit("platform:section", { productId: activeModule, section: item.id });
        setMobileSidebarOpen(false);
    }, [activeModule]);
    // onScreenSections несёт дверь, ДЛЯ КОТОРОЙ он создан, в самом замыкании (dep [activeEntryId]) —
    // новая дверь получает НОВЫЙ колбэк с правильным productId, а старый, даже если позднее его ещё
    // раз позовёт не успевший размонтироваться экран (просроченный промис/таймер), продолжает
    // штамповать СВОЙ старый productId — слушатель ниже его отбросит (payload.productId !==
    // activeProduct.id), а не перепишет чужие разделы. Доставка нового колбэка до уже смонтированного
    // экрана — через updateCtx (osnovaContext.ts, ModuleHost.tsx): без него ctx застывал бы на двери
    // первого mount() навсегда (обе двери продукта personnel/hr — один React-корень, entry.tsx).
    const onScreenSections = useCallback((sections) => {
        if (!activeEntryId)
            return;
        sharedEventBus.emit("platform:sections", { productId: activeEntryId, sections });
    }, [activeEntryId]);
    // Экран сам сообщает свой активный раздел (напр. пользователь кликнул
    // таб внутри тела экрана) — рельс подсвечивает activeItemId, ремоунта нет.
    const onScreenSectionChange = useCallback((id) => {
        setActiveItemId(id);
    }, []);
    const onScreenCrumbs = useCallback((crumbs) => {
        setBreadcrumbs(crumbs);
    }, []);
    // Раздел продукта — в адресе строки браузера (T-SHELL-SECTION-DEEPLINK): владелец копирует
    // ссылку на текущий экран (клик по разделу панели, живой platform:section от самого экрана,
    // приход по прямому URL /продукт/раздел — resolveDoorEntry выше). replaceState, не pushState —
    // смена раздела не плодит новую запись истории (там остаётся переход между дверями, navigate()).
    // Тем же местом запоминается последний раздел двери (lastItemForDoorRef, T-SHELL-DOOR-SWITCH-CONTENT) —
    // возврат на дверь кликом по рельсу подставит именно его, а не паспортный дефолт. T-286 §3: в
    // адрес уходит activeModule (слаг продукта), не activeEntryId (составной id входа) — id входа в
    // адрес не попадает никогда; search/hash сохраняются (там живёт, напр., ?deal=<id> уведомлений
    // CRM, T-287), сравнение с текущим адресом — только по pathname.
    useEffect(() => {
        if (!activeEntryId || activeEntryId === STORE_SLUG || !activeItemId || !activeModule)
            return;
        lastItemForDoorRef.current.set(activeEntryId, activeItemId);
        const segments = [activeModule, ...activeItemId.split("/")];
        const pathname = "/" + segments.map(encodeURIComponent).join("/");
        if (window.location.pathname !== pathname) {
            window.history.replaceState(null, "", pathname + window.location.search + window.location.hash);
        }
    }, [activeEntryId, activeModule, activeItemId]);
    const osnovaCtx = useMemo(() => {
        if (!activeEntryId || activeEntryId === STORE_SLUG || denied || !activeModule)
            return null;
        return createOsnovaContext({
            module: activeModule,
            view: activeItemId ?? undefined,
            theme, locale, numFmt,
            user: user ? { id: user.userId, email: user.email, displayName: user.displayName, isOwner: user.isOwner } : { id: "", email: "" },
            permissions: user?.perms ?? [],
            navigate,
            setBreadcrumbs,
            openModal,
            emitter: sharedEventBus,
            section: activeItemId,
            onSections: onScreenSections,
            onSectionChange: onScreenSectionChange,
            onCrumbs: onScreenCrumbs,
            installedProducts,
            productText: productTextApi,
        });
    }, [activeEntryId, activeModule, activeItemId, denied, theme, locale, numFmt, user, navigate, openModal, onScreenSections, onScreenSectionChange, onScreenCrumbs, installedProducts]);
    // T-SHELL-DOOR-SWITCH-CONTENT: клик по двери в рельсе всегда переключает рабочую область и
    // адрес СРАЗУ (тем же navigate(), что и деплинки), а не только перерисовывает подменю/заголовок —
    // старое поведение (peek: показать чужое меню без перехода, пока не кликнут пункт внутри него)
    // владелец воспроизвёл как баг: рабочая область и URL оставались от прежней двери. Стартовый
    // раздел новой двери подхватывают синхронные блоки выше (activeModuleOwnerRef/seededDoorRef/
    // lastEntrySeenRef) — паспортный дефолт либо, если дверь уже открывали в этой сессии,
    // lastItemForDoorRef. T-286 §3: адрес строится из слага продукта и раздела (запомненного или
    // стартового section найденной записи каталога), не из id входа — pickProduct больше не
    // передаёт navigate() составной id (p.id остаётся внутренним ключом lastItemForDoorRef/сравнения
    // активной двери, kit передаёт его назад тем же объектом, что получил в products, — RailProduct
    // кита своего поля product/section не несёт, поэтому полная запись берётся из productsRef).
    const pickProduct = useCallback((p) => {
        if (p.id === activeEntryId)
            return;
        const full = productsRef.current.find((x) => x.id === p.id);
        const module = full?.product ?? p.id;
        const section = lastItemForDoorRef.current.get(p.id) ?? full?.section;
        navigate(section ? { module, path: section } : { module });
    }, [activeEntryId, navigate]);
    const openStore = useCallback(() => {
        navigate({ module: STORE_SLUG });
    }, [navigate]);
    return {
        activeEntryId, activeProduct, activeModule, activeItemId, denied,
        osnovaCtx, navigate, installedProducts,
        mobileSidebarOpen, setMobileSidebarOpen,
        shownSlug, railMenu, shownLoading, shownActiveItemId, collapsedMenu,
        pickProduct, pickItem, openStore,
    };
}
